import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl, 
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Archive as ArchiveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { BglPrimaryButton, BglPrimarySmallButton, BglPrimaryOutlinedButton } from 'components/button/BglButtons';
import { BglOverlayLoader } from 'components/loading';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { BriefcaseIcon } from 'components/icons/BriefcaseIcon';
import { useDispatch, useSelector } from 'react-redux';
import { selectPilotList, selectPilot, setPilotList } from 'store/dataReducers/pilotsSlice';
import { PilotApis } from 'apis/pilotAPI';
import { FORM_PART_ONE_KEY, EDIT_PROJECT_DATA_KEY, FORM_PART_ONE_CONFIGS_KEY, FORM_PART_TWO_KEY, ENVIRONMENT_CREATION_DATA_KEY } from './UtilityCreationPage';
import { ProjectEnvironment } from 'types/apis/pilotInterfaces';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { BglSecondaryButton } from 'components/button/BglButtons'; 
import { useTheme } from '@mui/material/styles';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

// Mocked Project interface 
interface Project {
  id: string;
  projectId: string;
  name: string;
  description: string;
  status: string;
  projectType: string;
  awsRegion: string;
  projectImageUrl: string;
  buttonColor?: 'primary' | 'secondary';
  createdAt: Date;
  updatedAt: Date;
  logoSeed: string;
  ctaLink: string;
  ctaText: string;
  dataAiHint: string;
  environment?: ProjectEnvironment; // New single environment object
  projectEnvironments?: ProjectEnvironment[]; // Legacy support for backward compatibility
  tag?: string | string[]; // Tag can be a string or array of strings
}

type ViewMode = 'tile' | 'list';

let projectTypeOptions: any[] = [{ value: 'ALL', label: 'All Types' }];

let regionOptions: any[] = [{ value: 'ALL', label: 'All Regions' }];

export const UtilityListPage = () => {
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const theme = useTheme();
  const pilotList = useSelector(selectPilotList);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjectType, setSelectedProjectType] = useState('ALL');
  const [selectedRegion, setSelectedRegion] = useState('ALL');
  const [viewMode, setViewMode] = useState<ViewMode>('tile');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const hasLoadedForThisNavigation = useRef(false);

  const loadPilots = async () => {
    setIsLoadingData(true);
    try {
      const pilotsResp = await PilotApis.getPilots(rc);
      if (pilotsResp.error) {
        console.error('Error loading pilots:', pilotsResp.error);
      } else {
        console.log('Pilots loaded successfully:', pilotsResp.data?.data?.length || 0, pilotsResp.data?.data);
        
        // Set up project type options with predefined values
        projectTypeOptions = [
          { value: 'ALL', label: 'All Types' },
          { value: 'INTERNAL', label: 'Internal' },
          { value: 'ROLLOUT', label: 'Rollout' },
          { value: 'POC', label: 'POC' },
          { value: 'DEMO', label: 'Demo' },
        ];  

        regionOptions = [
          { value: 'ALL', label: 'All Regions' },
          ...Array.from(
            new Set(
              (pilotsResp.data?.data || [])
                .map((pilot: any) => pilot.awsRegion)
                .filter((region: any) => !!region)
            )
          ).map((region: any) => ({
            value: region,
            label: region.charAt(0).toUpperCase() + region.slice(1).toLowerCase()
          }))
        ];
        
        dispatch(setPilotList(pilotsResp.data?.data));
      }
    } catch (error) {
      console.error('Exception loading pilots:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (!hasLoadedForThisNavigation.current) {
      hasLoadedForThisNavigation.current = true;
      loadPilots();
    }
  }, []); // Empty dependency array - only run once when component mounts

  // Convert pilot data to Project format
  const convertPilotToProject = (pilot: any): Project => {  
    return {
      ...pilot,
      projectId: pilot.projectId || '',
      name: pilot.name || 'Unknown Project', 
      description: pilot.description || 'No description available',
      status: pilot.status || 'INPROGRESS',
      projectType: pilot.projectType || 'INTERNAL', // Default to INTERNAL
      awsRegion: pilot.awsRegion || 'us-west-1',
      projectImageUrl: pilot.projectImageUrl || 'https://picsum.photos/seed/modern-office/400/200',
      buttonColor: 'primary' as const,
      createdAt: new Date(pilot.createdAt || Date.now()),
      updatedAt: new Date(pilot.updatedAt || Date.now()),
      logoSeed: pilot.logoSeed || 'default-seed',
      ctaLink: '#',
      ctaText: 'Open Project',
      dataAiHint: pilot.dataAiHint || 'energy project',
      tag: pilot.tag || undefined // Support both 'tags' and 'tag' property names
    };
  };

  // Helper function to parse tags into an array of individual tag strings
  const parseTags = (tags: string | string[] | undefined): string[] => {
    if (!tags) return [];
    if (Array.isArray(tags)) {
      return tags
        .filter(tag => tag && tag.trim())
        .map(tag => tag.trim());
    }
    if (typeof tags === 'string') {
      const trimmed = tags.trim();
      if (!trimmed) return [];
      // If it's a comma-separated string, split it into individual tags
      return trimmed
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);
    }
    return [];
  };

  // Filter logic
  const filteredProjects = useMemo(() => {
    let projects = pilotList.map(convertPilotToProject);

    if (selectedProjectType !== 'ALL') {
      projects = projects.filter(project => project.projectType.toLowerCase() === selectedProjectType.toLowerCase());
    }

    if (selectedRegion !== 'ALL') {
      projects = projects.filter(project => project.awsRegion === selectedRegion);
    }

    if (searchTerm) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      projects = projects.filter(project =>
        project.name.toLowerCase().includes(lowercasedSearchTerm) ||
        project.description.toLowerCase().includes(lowercasedSearchTerm) ||
        project.awsRegion.toLowerCase().includes(lowercasedSearchTerm)
      );
    }

    return projects;
  }, [pilotList, searchTerm, selectedProjectType, selectedRegion]);

  const getProjectTypeColor = (projectType: string) => {
    switch (projectType.toLowerCase()) {
      case 'internal': return 'default';
      case 'rollout': return 'info';
      case 'poc': return 'secondary';
      case 'demo': return 'warning';
      default: return 'default';
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: Project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleEditProject = () => {
    if (selectedProject) {
      setIsLoading(true);
      try {
        // Store the project data for editing
        const editData = {
          ...selectedProject,
          // Step 1 data from the project
          projectId: selectedProject.projectId,
          name: selectedProject.name,
          description: selectedProject.description,
          status: selectedProject.status,
          awsRegion: selectedProject.awsRegion,
          projectImageUrl: selectedProject.projectImageUrl,
          // Add environment data in array format (for backward compatibility)
          projectEnvironments: [{ id: "", name: selectedProject.name + " Environment" }],
          // Also include single environment object if available
          environment: selectedProject.environment,
        };

        console.log('🔍 Debug - Edit data:', editData);
        
        sessionStorage.setItem(EDIT_PROJECT_DATA_KEY, JSON.stringify(editData));
        
        // Navigate to edit page
        rc.uiRouter.navigate(ROUTE_TREE.PROJECT.EDIT, { projectId: selectedProject.projectId });
      } finally {
        setIsLoading(false);
      }
    }
    handleMenuClose();
  };

  const handleArchiveProject = async () => {
    if (selectedProject) {
      try {
        setIsLoading(true);
        const response = await PilotApis.archiveProject(rc, selectedProject.projectId);
        
        if (response.error) {
          console.error('Error archiving project:', response.error);
          // You can add a toast notification here for error
          return;
        }
        
        // Success - refresh the project list
        await loadPilots();
        console.log('Project archived successfully');
        // You can add a toast notification here for success
        
      } catch (error) {
        console.error('Error archiving project:', error);
        // You can add a toast notification here for error
      } finally {
        setIsLoading(false);
      }
    }
    handleMenuClose();
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleOpenProject = async (correspondingPilot: Project) => {
    setIsLoading(true);
    try {
      console.log('Found matching pilot:', correspondingPilot.projectId);
      if (correspondingPilot) {
        // Select the pilot and navigate to pilot details
        dispatch(selectPilot({ projectId: correspondingPilot.projectId }));
        rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS);
      } else {
        // If no matching pilot found, show an alert with more helpful information
        const pilotNames = pilotList.map(p => p.name).join(', ');
        alert(`No matching pilot found for project.\n\nAvailable pilots: ${pilotNames || 'None loaded'}\n\nPlease ensure the pilot exists in the system or create it first.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewProject = () => {
    // Clear any existing session storage data to ensure fresh start
    sessionStorage.removeItem(FORM_PART_ONE_KEY);
    sessionStorage.removeItem(EDIT_PROJECT_DATA_KEY);
    sessionStorage.removeItem(FORM_PART_ONE_CONFIGS_KEY);
    sessionStorage.removeItem(FORM_PART_TWO_KEY);
    sessionStorage.removeItem(ENVIRONMENT_CREATION_DATA_KEY);
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CREATE);
  };

  // List View Component
  const ProjectListView = () => (
    <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 50 }}></TableCell>
            <TableCell>Project Name</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
            <TableCell sx={{ width: 120 }}>Project Type</TableCell>
            <TableCell sx={{ width: 100 }}>AWS Region</TableCell>
            <TableCell sx={{ width: 120 }}>PRS</TableCell>
            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Created</TableCell>
            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Last Updated</TableCell>
            {/* <TableCell align="right">Action</TableCell> */}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredProjects.map((project) => (
            <TableRow key={project.projectId} hover>
              <TableCell>
                <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </TableCell>
              <TableCell sx={{ fontWeight: 'medium' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {project.name}
                  {/* Tags Chips - Show individual chips for each tag */}
                  {parseTags(project.tag).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {parseTags(project.tag).map((tag, index) => (
                        <Chip 
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 20,
                            maxWidth: 'fit-content',
                            '& .MuiChip-label': {
                              padding: '0 6px',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}>
                {project.description}
              </TableCell>
              <TableCell>
                <Chip 
                  label={project.projectType.toLowerCase()} 
                  color={getProjectTypeColor(project.projectType)}
                  size="small"
                  variant="filled"
                  sx={{ textTransform: 'capitalize' }}
                />
              </TableCell>
              <TableCell>
                <Chip 
                  label={project.awsRegion} 
                  color="info"
                  variant="filled"
                  size="small"
                  icon={<LocationIcon />}
                  sx={{ boxShadow: 2, textTransform: 'uppercase' }}
                />
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}>
                62
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, color: 'text.secondary' }}>
                {formatDate(project.createdAt)}
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, color: 'text.secondary' }}>
                {formatDate(project.updatedAt)}
              </TableCell>
              {/* <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <BglPrimarySmallButton
                    fullWidth
                    sx={{ mr: 1 }}
                    onClick={() => handleOpenProject(project)}
                    disabled={isLoading}
                  >
                    {project.ctaText}
                  </BglPrimarySmallButton>
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleMenuOpen(e, project)}
                    disabled={isLoading}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </TableCell> */}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: 4, maxWidth: '100%', position: 'relative' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 4 }}>
        <Typography variant="bglTitle2" component="h1">
          Projects Dashboard
        </Typography>
        <BglPrimaryButton 
          startIcon={<AddCircleOutlineOutlinedIcon sx={{width: 16, height: 16,marginRight: 1}} />}
          sx={{ minWidth: 'fit-content', textTransform: 'none' }}
          onClick={handleCreateNewProject}
        >
          Create New Project
        </BglPrimaryButton>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        alignItems: 'center', 
        gap: 2, 
        mb: 4 
      }}>
        {/* Search */}
        <Box sx={{ position: 'relative', flexGrow: 1, width: { xs: '100%', md: 'auto' } }}>
          <SearchOutlinedIcon sx={{ 
            position: 'absolute', 
            left: 12, 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: '#6c7f93',
            zIndex: 9
          }} />
          <TextField
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              width: '100%',
              '& .MuiInputBase-root': { pl: 5 }
            }}
            size="small"
          />
        </Box>

        {/* Project Type Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
          <Select
            displayEmpty
            value={selectedProjectType}
            onChange={(e) => setSelectedProjectType(e.target.value)}
            startAdornment={<FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />}
          >
            {projectTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Region Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
          <Select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            startAdornment={<LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />}
          >
            {regionOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* View Mode Toggle */}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Tooltip title="Tile View">
            <IconButton 
              onClick={() => setViewMode('tile')}
              color={viewMode === 'tile' ? 'primary' : 'default'}
            >
              <GridIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="List View">
            <IconButton 
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
            >
              <ListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Projects Display */}
      {isLoadingData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <BglOverlayLoader open={isLoadingData} message="Loading projects..." />
        </Box>
      ) : filteredProjects.length > 0 ? (
        viewMode === 'tile' ? (
          <Grid container spacing={3}>
            {filteredProjects.map(project => (
              <Grid key={project.projectId} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Card sx={{ 
                  height: '100%', 
                  border: `1px solid ${theme.palette.secondary.light}`,
                  display: 'flex', 
                  borderRadius: '8px',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6
                  }
                }}>
                  {/* Project Image */}
                  <Box sx={{ position: 'relative', height: 160 }}>
                    <CardMedia
                      component="img"
                      height="160"
                      image={project?.projectImageUrl || `https://picsum.photos/seed/${project?.logoSeed || project?.name}/400/200`}
                      alt={project.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box sx={{ 
                      position: 'absolute', 
                      inset: 0, 
                      background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' 
                    }} />
                    
                    {/* Status Badges */}
                    <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Chip 
                        label={project.projectType.toLowerCase()} 
                        color={getProjectTypeColor(project.projectType)}
                        size="small"
                        variant="filled"
                        sx={{ textTransform: 'capitalize', boxShadow: 2 }}
                      />
                      <Chip 
                        label={project.awsRegion} 
                        color="info"
                        variant="filled"
                        size="small"
                        icon={<LocationIcon />}
                        sx={{ boxShadow: 2, textTransform: 'uppercase' }}
                      />
                      {/* Tags Chips - Show individual chips for each tag */}
                      {parseTags(project.tag).map((tag, index) => (
                        <Chip 
                          key={index}
                          label={tag}
                          size="small"
                          variant="filled"
                          sx={{ 
                            boxShadow: 2,
                            fontSize: '0.75rem',
                            maxWidth: '200px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }
                          }}
                        />
                      ))}
                    </Box>

                    {/* Project Title */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, p: 2,pb:1 }}>
                      <Box sx={{ color: 'white', fontSize: 32, mb: 1, opacity: 0.8, height: 32, width: 32 }}>
                        <BriefcaseIcon />
                      </Box>
                      <Typography 
                        variant="bglTitle4" 
                        sx={{ 
                          color: 'white', 
                          textTransform: 'capitalize',
                          fontSize: '20px',

                          // fontWeight: 'bold',
                          // textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                        }}
                      >
                        {project.name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Project Content */}
                  <CardContent sx={{ flexGrow: 1, p: 2, textAlign: 'left'}}>
                    <Typography variant="bglBody2" color="text.secondary" sx={{ mb: 2 }}>
                      {/* {project.description} */}
                      Comprehensive CX solutions for residential customers, focusing on engagement and satisfaction.
                    </Typography>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.secondary.light}`, justifyContent: 'space-between' }}>
                    <BglSecondaryButton 
                      fullWidth
                      sx={{ mr: 1}}
                      style={{textTransform: 'none'}}
                      onClick={() => handleOpenProject(project)}
                      disabled={isLoading}
                    >
                      {project.ctaText}
                    </BglSecondaryButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMenuOpen(e, project)}
                      disabled={isLoading}
                    >
                      <MoreVertIcon sx={{ color: theme.palette.secondary.contrastText }} />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <ProjectListView />
        )
      ) : (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="bglSubtitle1" sx={{ mb: 1 }}>
              No Projects Found
            </Typography>
            <Typography variant="bglBody2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || selectedProjectType !== 'ALL' || selectedRegion !== 'ALL'
                ? "Your search/filter criteria did not match any projects. Try adjusting your search or filters."
                : "There are no projects to display. You can create a new one."}
            </Typography>
            <BglPrimaryOutlinedButton 
              onClick={() => {
                setSearchTerm('');
                setSelectedProjectType('ALL');
                setSelectedRegion('ALL');
              }}
            >
              Clear Search & Filters
            </BglPrimaryOutlinedButton>
          </CardContent>
        </Card>
      )}

      {/* Project Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* <MenuItem onClick={handleEditProject}>
          <ListItemIcon>
            <EditIcon fontSize="small" style={{ color: theme.palette.secondary.contrastText }}/>
          </ListItemIcon>
          <ListItemText>Update Project</ListItemText>
        </MenuItem> */}
        {/* <MenuItem onClick={handleArchiveProject}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive Project</ListItemText>
        </MenuItem> */}
        {/* <Divider /> */}
        <MenuItem onClick={handleArchiveProject} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <ArchiveOutlinedIcon fontSize="small" style={{ color: theme.palette.secondary.contrastText }}/>
          </ListItemIcon>
          <ListItemText>Archive Project</ListItemText>
        </MenuItem>
      </Menu>

      {/* Overlay Loader for Operations */}
      <BglOverlayLoader
        open={isLoading}
        message="Processing..."
        size={50}
      />
    </Box>
  );
};